import { type Home, type ResidentBody, UserRole } from '@lmc/ts-common'
import {
  Edit,
  Form,
  FormItem,
  FormRadio,
  FormRadioGroup,
  Input,
  Modal,
  Trash
} from '@lmc/ui-kit'
import { Button, Heading, Text } from '@lmc/ui-kit/src/general'
import type { ResidentFetchItemResponse } from '@lmc/validations'
import { Form as AntdForm } from 'antd'
import { formatAddress, getNominative, showToast } from 'common/utils'
import { useRoleAccess } from 'components/AccessControl'
import { ConfirmationBody } from 'components/forms/screens/ResidentEditForm'
import dayjs from 'dayjs'
import useFeatureFlag from 'hooks/useFeatureFlags'
import useHomeType from 'hooks/useHomeType'
import { useLatestTransferRecord } from 'hooks/useLatestTransferRecord'
import useMixpanelEvent, { MixpanelEvents } from 'hooks/useMixpanelEvent'
import useResident from 'hooks/useResident'
import { useTransferRecordCreate } from 'hooks/useTransferRecordCreate'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { transferRecordConstants as trConstants } from 'locale/transferRecords'
import { capitalize } from 'lodash'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import DeactivatingServiceUserModal from 'screens/Rostering/ChangeManagement/DeactivatingServiceUserModal'
import DeathDetailsModal from 'screens/Rostering/ChangeManagement/DeathDetailsModal'
import DeletingServiceUserModal from 'screens/Rostering/ChangeManagement/DeletingServiceUserModal'
import ReactivatingServiceUserModal from 'screens/Rostering/ChangeManagement/ReactivatingServiceUserModal'
import TransferDetailsModal from 'screens/Rostering/ChangeManagement/TransferDetailsModal'
import styled from 'styled-components'
import type { DeathDetails } from 'types/deathDetails'
import { ResidentStatusEnum } from 'types/residentStatus'
import type {
  TransferRecordPayload,
  TransferRecordType
} from 'types/transferRecord'
import { GeneralText } from '../styledComponents'
import { RESIDENT_STATUS_OPTIONS } from '../utils/profileScreenFields'
import { matchesName } from '../utils/utils'

type GeneralStatusModalProps = {
  resident: ResidentFetchItemResponse
  testId?: string
}

export const GeneralStatusModal = ({
  resident,
  testId
}: GeneralStatusModalProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const [showModal, setShowModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isDeleteEnabled, setIsDeleteEnabled] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [saveLoading, setSaveLoading] = useState(false)
  const [form] = AntdForm.useForm()
  const navigate = useNavigate()
  const { hasRota } = useHomeType()
  const sendMixpanelEvent = useMixpanelEvent()

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )
  const residentNominative = getNominative(
    preferredPeopleNominative,
    'Resident'
  )

  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )
  const homeGroup = useSelector(
    (state: {
      global: { home: Home }
      data: { homes: { id: string; group?: string }[] }
    }) => state.data.homes?.find((h) => h.id === state.global.home.id)?.group
  )

  const CARE_SETTING_LABELS: Record<string, string> = {
    Dom: t(trConstants.CARE_SETTING_DOMICILIARY, {
      ns: NAMESPACE.TRANSFER_RECORDS
    }),
    MH: t(trConstants.CARE_SETTING_MENTAL_HEALTH, {
      ns: NAMESPACE.TRANSFER_RECORDS
    }),
    LD: t(trConstants.CARE_SETTING_LEARNING_DISABILITIES, {
      ns: NAMESPACE.TRANSFER_RECORDS
    })
  }

  const getCareSettingTypeFromGroup = (group?: string): string => {
    if (!group) return ''
    return CARE_SETTING_LABELS[group] ?? group
  }

  const [showDeactivatingSUModal, setShowDeactivatingSUModal] = useState(false)
  const [showReactivatingSUModal, setShowReactivatingSUModal] = useState(false)
  const [showDeletingSUModal, setShowDeletingSUModal] = useState(false)
  const [showDeathDetailsModal, setShowDeathDetailsModal] = useState(false)
  const [showTransferDetailsModal, setShowTransferDetailsModal] =
    useState(false)
  const [transferType, setTransferType] =
    useState<TransferRecordType>('hospitalisation')
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>(
    resident?.status
  )

  const flags = useFeatureFlag({
    flagNames: ['rostering_change_management', 'transfer_records']
  })?.data

  const flagsRosteringChangeManagementEnabled =
    flags?.rostering_change_management?.enabled

  const transferRecordsEnabled = flags?.transfer_records?.enabled

  const residentStatus = resident?.status as unknown as ResidentStatusEnum

  const isComingFromTransferStatus =
    residentStatus === ResidentStatusEnum.hospitalised ||
    residentStatus === ResidentStatusEnum.transferred

  const { latestRecord } = useLatestTransferRecord(
    resident.id,
    isComingFromTransferStatus && !!transferRecordsEnabled
  )

  const { createTransferRecord } = useTransferRecordCreate({
    residentId: resident.id,
    onError: () => {
      showToast({
        title: t(trConstants.TRANSFER_RECORD_FAILED, {
          ns: NAMESPACE.TRANSFER_RECORDS
        }),
        type: 'error'
      })
    }
  })

  const { updateResident, deleteResident, refetch } = useResident({
    id: resident.id,
    onUpdateSuccess: () => {
      showToast({
        title: t(supConstants.RESIDENT_UPDATED, {
          variable: residentNominative
        })
      })

      refetch()
    },
    onDeleteSuccess: () => {
      showToast({
        title: t(supConstants.RESIDENT_DELETED, {
          variable: residentNominative
        })
      })
      navigate('/residents/list')
    }
  })

  const handleOnFinish = async (
    values: Partial<ResidentBody>,
    deathDetails?: DeathDetails
  ) => {
    setSaveLoading(true)
    const updateData = deathDetails ? { ...values, ...deathDetails } : values
    const response = await updateResident(updateData)

    if (response && typeof response === 'object' && 'result' in response) {
      sendMixpanelEvent(MixpanelEvents.RESIDENT_STATUS_UPDATED, {
        previousStatus: resident.status,
        newStatus: values.status,
        ...(deathDetails?.deathDate && { deathDate: deathDetails.deathDate }),
        ...(deathDetails?.deathDetails && {
          deathDetails: deathDetails.deathDetails
        })
      })
    }

    setSaveLoading(false)
    setShowModal(false)
    setShowDeathDetailsModal(false)
    setShowTransferDetailsModal(false)

    if (hasRota && flagsRosteringChangeManagementEnabled) {
      setShowDeactivatingSUModal(false)
      setShowReactivatingSUModal(false)
      setShowDeletingSUModal(false)
    }
  }

  const handleDeathDetailsConfirm = (deathDetails: DeathDetails) => {
    const formValues = form.getFieldsValue()
    handleOnFinish(formValues, deathDetails)
  }

  const handleTransferDetailsConfirm = async (
    transferData: TransferRecordPayload
  ) => {
    setSaveLoading(true)
    await createTransferRecord(transferData).catch(() => {})
    await handleOnFinish(form.getFieldsValue())
  }

  const handleDeleteProfile = () => {
    setDeleteLoading(true)
    deleteResident()
  }

  const handleConfirmModal = () => {
    const status = form.getFieldValue('status')

    if (status === ResidentStatusEnum.deceased && !isDeleting) {
      setShowDeathDetailsModal(true)
      return
    }

    if (
      status === ResidentStatusEnum.hospitalised &&
      !isDeleting &&
      transferRecordsEnabled
    ) {
      setTransferType('hospitalisation')
      setShowTransferDetailsModal(true)
      return
    }

    if (
      status === ResidentStatusEnum.transferred &&
      !isDeleting &&
      transferRecordsEnabled
    ) {
      setTransferType('transfer')
      setShowTransferDetailsModal(true)
      return
    }

    if (
      status === ResidentStatusEnum.active &&
      isComingFromTransferStatus &&
      !isDeleting &&
      transferRecordsEnabled
    ) {
      setTransferType('return')
      setShowTransferDetailsModal(true)
      return
    }

    if (
      status === ResidentStatusEnum.discharged &&
      !isDeleting &&
      transferRecordsEnabled
    ) {
      setTransferType('discharge')
      setShowTransferDetailsModal(true)
      return
    }

    if (hasRota && flagsRosteringChangeManagementEnabled) {
      if (status === ResidentStatusEnum.active) {
        setShowReactivatingSUModal(true)
      } else {
        setShowDeactivatingSUModal(true)
      }
    } else if (isDeleting) {
      handleDeleteProfile()
    } else {
      form.submit()
    }
  }

  const isDeceasedSelected = selectedStatus === ResidentStatusEnum.deceased
  const isHospitalisedSelected =
    selectedStatus === ResidentStatusEnum.hospitalised
  const isTransferredSelected =
    selectedStatus === ResidentStatusEnum.transferred
  const isReturnToActive =
    selectedStatus === ResidentStatusEnum.active && isComingFromTransferStatus
  const isDischargedSelected = selectedStatus === ResidentStatusEnum.discharged
  const showNextButton =
    isDeceasedSelected ||
    (!!transferRecordsEnabled &&
      (isHospitalisedSelected ||
        isTransferredSelected ||
        isReturnToActive ||
        isDischargedSelected))

  const handleDeleteClick = () => {
    if (hasRota && flagsRosteringChangeManagementEnabled)
      setShowDeletingSUModal(true)
    else setIsDeleting(true)
  }

  const fullName = `${resident.name.first} ${resident.name.last}`

  const hasRolePermissionToDelete = useRoleAccess(
    [UserRole.careHomeAdmin],
    [
      {
        i: 'delete',
        this: 'Resident'
      }
    ]
  )

  const hasRolePermissionToSave = useRoleAccess(
    [UserRole.careHomeAdmin],
    [
      {
        i: 'update',
        this: 'Resident'
      }
    ]
  )

  return (
    <>
      <ButtonWrapper
        onClick={() => setShowModal(true)}
        data-testid={
          testId ? `${testId}-status-button` : 'resident-status-button'
        }
      >
        <InnerWrapper
          data-testid={
            testId ? `${testId}-status-info` : 'resident-status-info'
          }
        >
          <GeneralText
            weight="bold"
            data-testid={
              testId ? `${testId}-status-text` : 'resident-status-text'
            }
          >
            {capitalize(resident?.status)}
          </GeneralText>
          {resident.statusChangedOn && (
            <StyledText
              level={2}
              data-testid={
                testId
                  ? `${testId}-status-changed-date`
                  : 'resident-status-changed-date'
              }
            >
              {t(supConstants.CHANGED)}:{' '}
              {dayjs(resident.statusChangedOn).format('DD/MM/YYYY')}
            </StyledText>
          )}
        </InnerWrapper>
        <IconWrapper
          data-cy="userEditBtn"
          data-testid={
            testId ? `${testId}-status-edit-icon` : 'resident-status-edit-icon'
          }
        >
          <Edit />
        </IconWrapper>
      </ButtonWrapper>
      <Modal
        localisation={{
          title: isDeleting
            ? t(supConstants.DELETE_PROFILE)
            : t(supConstants.EDIT_STATUS),
          confirmText: isDeleting
            ? t(supConstants.DELETE_PROFILE)
            : showNextButton
              ? t(supConstants.NEXT)
              : t(supConstants.SAVE),
          cancelText: t(supConstants.CANCEL)
        }}
        onCancel={() => {
          setShowModal(false)
          setIsDeleting(false)
          setIsDeleteEnabled(false)
          setSelectedStatus(resident?.status)
          form.setFieldsValue({ status: resident?.status })
        }}
        isModalVisible={showModal}
        closeButton
        closeOnConfirm
        onConfirm={handleConfirmModal}
        noPadding
        data-testid={
          testId ? `${testId}-status-modal` : 'resident-status-modal'
        }
        confirmButtonProps={{
          isLoading: isDeleting ? deleteLoading : saveLoading,
          disabled:
            (isDeleting && !isDeleteEnabled) ||
            (!isDeleting && selectedStatus === resident?.status),
          variant: isDeleting ? 'danger' : 'primary',
          disabledByPermission: !hasRolePermissionToSave
        }}
        extraFooterElement={
          hasRolePermissionToDelete &&
          !isDeleting && (
            <DeleteButtonWrapper
              data-testid={
                testId
                  ? `${testId}-delete-button-wrapper`
                  : 'resident-delete-button-wrapper'
              }
            >
              <Trash />
              <Button
                data-cy="delete-btn"
                variant="text"
                onClick={handleDeleteClick}
                data-testid={
                  testId ? `${testId}-delete-button` : 'resident-delete-button'
                }
              >
                <DeleteButtonText>
                  {t(supConstants.DELETE_PROFILE)}
                </DeleteButtonText>
              </Button>
            </DeleteButtonWrapper>
          )
        }
      >
        {isDeleting ? (
          <>
            <div
              data-testid={
                testId
                  ? `${testId}-delete-confirmation`
                  : 'resident-delete-confirmation'
              }
            >
              {ConfirmationBody(resident.name, preferredPeopleNominative)}
            </div>
            <Input
              placeholder={fullName}
              onChange={(e) => {
                if (matchesName(e.target.value, fullName)) {
                  setIsDeleteEnabled(true)
                } else {
                  setIsDeleteEnabled(false)
                }
              }}
              data-testid={
                testId
                  ? `${testId}-delete-name-input`
                  : 'resident-delete-name-input'
              }
            />
          </>
        ) : (
          <ModalContent
            data-testid={
              testId
                ? `${testId}-status-form-content`
                : 'resident-status-form-content'
            }
          >
            <StyledHeading
              level={4}
              data-testid={
                testId
                  ? `${testId}-status-form-title`
                  : 'resident-status-form-title'
              }
            >
              {t(supConstants.WHAT_IS_CURRENT)}
            </StyledHeading>
            <Form
              form={form}
              onFinish={handleOnFinish}
              initialValues={resident}
              data-testid={
                testId ? `${testId}-status-form` : 'resident-status-form'
              }
            >
              <FormItem
                name="status"
                data-testid={
                  testId
                    ? `${testId}-status-form-item`
                    : 'resident-status-form-item'
                }
              >
                <FormRadioGroup
                  value={form.getFieldValue('status')}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  data-testid={
                    testId
                      ? `${testId}-status-radio-group`
                      : 'resident-status-radio-group'
                  }
                >
                  {RESIDENT_STATUS_OPTIONS.filter(
                    (opt) =>
                      transferRecordsEnabled ||
                      opt.value !== ResidentStatusEnum.transferred
                  ).map((residentStatusOption) => (
                    <FormRadio
                      data-cy={`radio-${residentStatusOption.label
                        .split(' ')
                        .slice(0, 1)}`}
                      key={residentStatusOption.value}
                      value={residentStatusOption.value}
                      text={residentStatusOption.label}
                      data-testid={
                        testId
                          ? `${testId}-status-radio-${residentStatusOption.value}`
                          : `resident-status-radio-${residentStatusOption.value}`
                      }
                    />
                  ))}
                </FormRadioGroup>
              </FormItem>
            </Form>
          </ModalContent>
        )}
      </Modal>
      {showDeactivatingSUModal && (
        <DeactivatingServiceUserModal
          showModal={showDeactivatingSUModal}
          onCancel={() => {
            setShowDeactivatingSUModal(false)
            setShowModal(false)
          }}
          onConfirm={() => {
            form.submit()
          }}
          name={fullName}
          status={form.getFieldValue('status')}
          backNavigation={() => {
            setShowDeactivatingSUModal(false)
          }}
        />
      )}
      {showReactivatingSUModal && (
        <ReactivatingServiceUserModal
          showModal={showReactivatingSUModal}
          onCancel={() => {
            setShowReactivatingSUModal(false)
            setShowModal(false)
          }}
          onConfirm={() => form.submit()}
          name={fullName}
          backNavigation={() => setShowReactivatingSUModal(false)}
        />
      )}
      {showDeletingSUModal && (
        <DeletingServiceUserModal
          showModal={showDeletingSUModal}
          onCancel={() => {
            setShowDeletingSUModal(false)
            setShowModal(false)
          }}
          onConfirm={() => handleDeleteProfile()}
          name={fullName}
          backNavigation={() => setShowDeletingSUModal(false)}
        />
      )}
      {showDeathDetailsModal && (
        <DeathDetailsModal
          showModal={showDeathDetailsModal}
          onCancel={() => {
            setShowDeathDetailsModal(false)
            setShowModal(false)
            setSelectedStatus(resident?.status)
            form.resetFields()
          }}
          onConfirm={handleDeathDetailsConfirm}
          name={fullName}
          backNavigation={() => {
            setShowDeathDetailsModal(false)
            setShowModal(true)
          }}
          initialValues={{
            deathDate: resident.deathDate,
            deathDetails: resident.deathDetails
          }}
          isLoading={saveLoading}
          showRotaWarning={hasRota && flagsRosteringChangeManagementEnabled}
        />
      )}
      {showTransferDetailsModal && (
        <TransferDetailsModal
          showModal={showTransferDetailsModal}
          onCancel={() => {
            setShowTransferDetailsModal(false)
            setShowModal(false)
            setSelectedStatus(resident?.status)
            form.resetFields()
          }}
          onConfirm={handleTransferDetailsConfirm}
          name={fullName}
          type={transferType}
          backNavigation={() => {
            setShowTransferDetailsModal(false)
            setShowModal(true)
          }}
          initialValues={
            transferType === 'return' && latestRecord
              ? {
                  transferredFromLocationName:
                    latestRecord.transferredTo?.locationName,
                  transferredFromAddress: latestRecord.transferredTo?.address,
                  transferredFromCareSettingType:
                    latestRecord.transferredTo?.careSettingType,
                  transferredToLocationName:
                    latestRecord.transferredFrom?.locationName,
                  transferredToAddress: latestRecord.transferredFrom?.address,
                  transferredToCareSettingType:
                    latestRecord.transferredFrom?.careSettingType
                }
              : {
                  transferredFromLocationName: home?.name,
                  transferredFromAddress: home?.address
                    ? formatAddress(home.address)
                    : undefined,
                  transferredFromCareSettingType:
                    getCareSettingTypeFromGroup(homeGroup)
                }
          }
          originalReason={
            transferType === 'return' && latestRecord
              ? latestRecord.reason
              : undefined
          }
          linkedTransferRecord={
            transferType === 'return' && latestRecord
              ? latestRecord._id
              : undefined
          }
          isLoading={saveLoading}
          showRotaWarning={hasRota && flagsRosteringChangeManagementEnabled}
        />
      )}
    </>
  )
}

const ButtonWrapper = styled.div`
  border-color: ${(props) => props.theme.color.neutral.color8} !important;
  border: 1px solid;
  padding: 8px 16px;
  border-radius: ${(props) => props.theme.properties.borderRadius.medium};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  &:hover {
    border-color: ${({ theme }) => theme.color.status.primary} !important;
  }
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    margin-left: auto;
    margin-right: auto;
  }
`

const InnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
`

const DeleteButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`

const ModalContent = styled.div`
  border-top: 1px solid;
  border-bottom: 1px solid;
  color: ${(props) => props.theme.color.neutral.color8};
  padding-bottom: 16px;
  padding-top: 16px;
`

const DeleteButtonText = styled(Text)`
  color: ${(props) => props.theme.color.status.danger};
  margin-left: 2px;
`

const StyledHeading = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
  margin-bottom: 15px;
`

const StyledText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    text-align: center;
  }
`
